import { useEffect, useState, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Bell, Menu, X, LogOut, Pill } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SiteFooter from "@/components/SiteFooter";

const NAV = {
  worker: [
    { to: "/worker", label: "Home" },
    { to: "/worker/opportunities", label: "Opportunities" },
    { to: "/worker/requests", label: "Requests" },
    { to: "/worker/calendar", label: "Calendar" },
    { to: "/worker/availability", label: "Availability" },
    { to: "/worker/profile", label: "Profile" },
    { to: "/settings", label: "Settings" },
  ],
  pharmacy: [
    { to: "/pharmacy", label: "Dashboard" },
    { to: "/pharmacy/post", label: "Post Shift" },
    { to: "/pharmacy/staffing", label: "Staffing" },
    { to: "/pharmacy/calendar", label: "Calendar" },
    { to: "/pharmacy/profile", label: "Pharmacy Profile" },
    { to: "/settings", label: "Settings" },
  ],
  admin: [
    { to: "/admin", label: "Dashboard" },
    { to: "/admin/accounts", label: "Accounts" },
    { to: "/admin/workers", label: "Workers" },
    { to: "/admin/pharmacies", label: "Pharmacies" },
    { to: "/admin/shifts", label: "Shifts" },
    { to: "/admin/matches", label: "Matches" },
    { to: "/admin/reports", label: "Reports" },
    { to: "/admin/legal", label: "Legal" },
  ],
};

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [panel, setPanel] = useState(false);

  const links = NAV[user?.role] || [];

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifs(data);
    } catch {
      // notifications are non-critical chrome
    }
  }, []);

  useEffect(() => {
    load();
    setOpen(false);
    setPanel(false);
  }, [location.pathname, load]);

  const unread = notifs.filter((n) => !n.read).length;

  const markAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  const openNotif = async (n) => {
    await api.post(`/notifications/${n.notification_id}/read`);
    setPanel(false);
    if (n.link) navigate(n.link);
    else load();
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/85 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center gap-4 px-4 sm:px-6">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand text-white">
              <Pill className="h-4 w-4" />
            </span>
            <span className="font-display text-lg font-semibold tracking-tight">FindRxStaff</span>
          </Link>

          <nav className="ml-6 hidden items-center gap-1 lg:flex">
            {links.map((l) => {
              const active = location.pathname === l.to;
              return (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`rounded-full px-3.5 py-2 text-sm transition-colors ${
                    active ? "bg-brand-soft font-medium text-brand" : "text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  {l.label}
                </Link>
              );
            })}
          </nav>

          <div className="ml-auto flex items-center gap-2">
            <div className="relative">
              <button
                onClick={() => setPanel(!panel)}
                data-testid="notifications-button"
                className="relative rounded-full p-2 text-slate-600 transition-colors hover:bg-slate-100"
              >
                <Bell className="h-5 w-5" />
                {unread > 0 && (
                  <span
                    className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-accentx px-1 text-[10px] font-semibold text-white"
                    data-testid="notifications-unread-count"
                  >
                    {unread}
                  </span>
                )}
              </button>
              {panel && (
                <div
                  className="absolute right-0 mt-2 w-[22rem] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl"
                  data-testid="notifications-panel"
                >
                  <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
                    <span className="overline">Notifications</span>
                    <button onClick={markAll} className="text-xs font-medium text-brand" data-testid="mark-all-read">
                      Mark all read
                    </button>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifs.length === 0 && (
                      <p className="px-4 py-6 text-sm text-slate-500">No notifications yet.</p>
                    )}
                    {notifs.map((n) => (
                      <button
                        key={n.notification_id}
                        onClick={() => openNotif(n)}
                        data-testid={`notification-${n.notification_id}`}
                        className={`block w-full border-b border-slate-50 px-4 py-3 text-left hover:bg-slate-50 ${
                          n.read ? "opacity-60" : ""
                        }`}
                      >
                        <p className="text-sm font-medium text-slate-900">{n.title}</p>
                        <p className="mt-0.5 text-xs text-slate-500">{n.message}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={async () => {
                await logout();
                navigate("/");
              }}
              data-testid="logout-button"
              className="hidden items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm text-slate-600 transition-colors hover:bg-slate-50 sm:flex"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </button>

            <button
              className="rounded-full p-2 text-slate-600 lg:hidden"
              onClick={() => setOpen(!open)}
              data-testid="mobile-menu-button"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {open && (
          <nav className="border-t border-slate-200 bg-white px-4 pb-4 pt-2 lg:hidden" data-testid="mobile-nav">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="block rounded-lg px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50"
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {l.label}
              </Link>
            ))}
            <button
              onClick={async () => {
                await logout();
                navigate("/");
              }}
              className="mt-2 block w-full rounded-lg px-3 py-2.5 text-left text-sm text-slate-500"
              data-testid="mobile-logout"
            >
              Sign out
            </button>
          </nav>
        )}
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-10">{children}</main>
      <SiteFooter compact />
    </div>
  );
}
